import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import stepsStyles from "../steps.module.css";
import { useCedulaLookup } from "../../../../residents/services/useCedulaLookup";

export default function StepIdentificacion({ formData, setFormData, errors = {} }) {
  const { t } = useTranslation();

  const TIPO_CEDULA_OPTS = [
    { value: "FISICA",     label: t("proveedores.tipoFisica") },
    { value: "JURIDICA",   label: t("proveedores.tipoJuridica") },
    { value: "DIMEX",      label: "DIMEX" },
    { value: "NITE",       label: "NITE" },
    { value: "PASAPORTE",  label: t("proveedores.tipoPasaporte") },
    { value: "EXTRANJERO", label: t("proveedores.tipoExtranjero") },
    { value: "OTRO",       label: t("proveedores.tipoOtro") },
  ];
  const { buscar, resultado, estado, reset } = useCedulaLookup();
  const [autoloaded, setAutoloaded] = useState(false);

  // Cuando llega resultado del TSE / base local, rellenar el nombre completo
  useEffect(() => {
    if (!resultado || autoloaded) return;
    // Para personas físicas: nombre + apellidos
    // Para personas jurídicas: nombre_sociedad (siempre en MAYÚSCULAS)
    const nombre = resultado.nombre_sociedad
      ? resultado.nombre_sociedad.toUpperCase()
      : [resultado.nombre, resultado.apellidos].filter(Boolean).join(" ") ||
        resultado.nombre ||
        "";
    if (nombre) {
      setFormData((prev) => ({
        ...prev,
        nombre,
      }));
    }
    setAutoloaded(true);
  }, [resultado, autoloaded, setFormData]);

  function handleChange(field, value) {
    if (field === "cedula") {
      setAutoloaded(false);
      reset();
    }
    // Razón social de una sociedad — siempre en MAYÚSCULAS, sin importar
    // si vino del TSE o se escribió a mano.
    if (field === "nombre" && formData.tipo_cedula === "JURIDICA") {
      value = value.toUpperCase();
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  const estadoColor = { local: "#4ade80", tse: "#60a5fa" }[estado];

  return (
    <div className="step">
      <h3 className="p-0 mt-2 mb-0 title" style={{ fontSize: "1.3rem" }}>
        {t("proveedores.identificacionTitulo")}
      </h3>
      <p className={`${stepsStyles.inactive} mb-3`} style={{ fontSize: "1rem" }}>
        {t("proveedores.identificacionSubtitulo")}
      </p>

      <div className={stepsStyles["form-wrapper"]}>
        {/* Tipo de identificación */}
        <div className="form-group">
          <label className="form-label">
            {t("proveedores.tipoIdentificacion")} <span style={{ color: "#f87171" }}>*</span>
          </label>
          <select
            value={formData.tipo_cedula}
            onChange={(e) => handleChange("tipo_cedula", e.target.value)}
            className={`${stepsStyles["form-control"]} form-control`}
            style={{ borderColor: errors.tipo_cedula ? "#ef4444" : undefined }}
          >
            {TIPO_CEDULA_OPTS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Número de identificación */}
        <div className="form-group">
          <label className="form-label">
            {t("proveedores.numeroIdentificacion")} <span style={{ color: "#f87171" }}>*</span>
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1, position: "relative" }}>
              <input
                type="text"
                className={`${stepsStyles["form-control"]} form-control`}
                style={{ borderColor: errors.cedula ? "#ef4444" : undefined }}
                placeholder="Ej: 3-102-000000"
                value={formData.cedula}
                onChange={(e) => handleChange("cedula", e.target.value)}
                onBlur={() => {
                  if (formData.cedula.replace(/\D/g, "").length >= 9)
                    buscar(formData.cedula);
                }}
              />
              {estado === "buscando" && (
                <i
                  className="fa-solid fa-circle-notch fa-spin"
                  style={{
                    position: "absolute", right: 10, top: "50%",
                    transform: "translateY(-50%)", color: "#fbbf24",
                  }}
                />
              )}
            </div>
            <button
              type="button"
              onClick={() => buscar(formData.cedula)}
              disabled={estado === "buscando"}
              style={{
                padding: "0 12px",
                background: "rgba(249,115,22,0.15)",
                border: "1px solid rgba(249,115,22,0.4)",
                borderRadius: 6, color: "#f97316",
                cursor: estado === "buscando" ? "not-allowed" : "pointer",
                fontSize: "0.78rem", whiteSpace: "nowrap",
              }}
            >
              {estado === "buscando"
                ? <i className="fa-solid fa-circle-notch fa-spin" />
                : <><i className="fa-solid fa-magnifying-glass" style={{ marginRight: 4 }} />{t("common.buscar")}</>}
            </button>
          </div>
          {estadoColor && (
            <p style={{ margin: "5px 0 0", fontSize: "0.75rem", color: estadoColor }}>
              <i className={`fa-solid fa-${estado === "local" ? "database" : "globe"}`} style={{ marginRight: 4 }} />
              {estado === "local" ? t("proveedores.encontradoSistema") : t("proveedores.datosTSE")}
            </p>
          )}
          {estado === "no_encontrado" && (
            <p style={{ margin: "5px 0 0", fontSize: "0.75rem", color: "var(--text-muted)" }}>
              {t("proveedores.noEncontrado")}
            </p>
          )}
          {errors.cedula && (
            <p style={{ margin: "4px 0 0", fontSize: "0.75rem", color: "#f87171" }}>{errors.cedula}</p>
          )}
        </div>

        {/* Nombre completo / razón social */}
        <div className="form-group">
          <label className="form-label">
            {t("proveedores.nombreRazonSocial")} <span style={{ color: "#f87171" }}>*</span>
          </label>
          <input
            type="text"
            className={`${stepsStyles["form-control"]} form-control`}
            style={{ borderColor: errors.nombre ? "#ef4444" : undefined, fontSize: "15px" }}
            placeholder={t("proveedores.nombrePlaceholder")}
            value={formData.nombre}
            onChange={(e) => handleChange("nombre", e.target.value)}
          />
          <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 3 }}>
            {t("proveedores.nombreHint")}
          </div>
          {errors.nombre && (
            <p style={{ margin: "4px 0 0", fontSize: "0.75rem", color: "#f87171" }}>{errors.nombre}</p>
          )}
        </div>

        {/* Activo */}
        <div className="form-group" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input
            id="prov_activo"
            type="checkbox"
            checked={formData.activo}
            onChange={(e) => handleChange("activo", e.target.checked)}
            style={{ cursor: "pointer", width: 16, height: 16 }}
          />
          <label htmlFor="prov_activo" className="form-label mb-0" style={{ cursor: "pointer" }}>
            {t("proveedores.proveedorActivo")}
          </label>
        </div>
      </div>
    </div>
  );
}
